import { useFormContext, useWatch } from 'react-hook-form';
import type { IGetMilestonesServiceRes } from '@/api/Sherpa/Project/modules/ProjectTdm/types';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { useMilestones } from '@/hooks/ReactQueryHooks/useMilestonesQuery';
import useUpdateEffect from '@/hooks/useUpdateEffect';
import { capitalize, formatDate3 } from '@/utils';
import type { MilestoneChangeOrderSchema } from '../MilestoneForm/validationSchema';

type MilestoneOption = {
  label: string;
  value: string;
  dueDate?: string | null;
};

const isMilestoneOptionEqual = (option: unknown, value: unknown) =>
  (option as MilestoneOption)?.value === (value as MilestoneOption)?.value;

export function MilestoneSelect({ projectId, isDisabled }: { projectId: string; isDisabled?: boolean }) {
  const formMethods = useFormContext<MilestoneChangeOrderSchema>();
  const phase = useWatch({
    name: 'phase',
    control: formMethods.control,
  });
  const milestone = useWatch({
    name: 'milestone',
    control: formMethods.control,
  });

  const { data, isFetching } = useMilestones({
    params: {
      page: 1,
      perPage: 100,
      project_id: Number(projectId),
      phase: phase?.value,
    },
    enabled: Boolean(phase?.value),
  });

  const milestones = data as IGetMilestonesServiceRes | undefined;
  const milestoneOptions = milestones?.data.map((milestone) => ({
    label: milestone.name,
    value: String(milestone.id),
    dueDate: milestone.dueDate,
  }));

  useUpdateEffect(() => {
    formMethods.setValue('milestone', null);
    formMethods.setValue('dueDate', '');
    formMethods.setValue('proposedDueDate', '');
  }, [phase?.value]);

  useUpdateEffect(() => {
    const currentMilestone = milestones?.data.find((option) => String(option.id) === String(milestone?.value));

    if (currentMilestone) {
      formMethods.setValue('proposedDueDate', formatDate3(currentMilestone.dueDate, 'yyyy-mm-dd'));
      formMethods.setValue('dueDate', formatDate3(currentMilestone.dueDate, 'yyyy-mm-dd'));

      const { phase } = formMethods.getValues();

      if (!phase) {
        formMethods.setValue('phase', {
          label: capitalize(currentMilestone.phase),
          value: currentMilestone.phase.toUpperCase(),
        });
      }
    }
  }, [milestone]);

  return (
    <HookReactSingleSelect
      formMethods={formMethods}
      label='Milestone'
      options={milestoneOptions ?? []}
      placeholder=''
      isDisabled={isDisabled || !phase?.value}
      isLoading={isFetching}
      isOptionEqualToValue={isMilestoneOptionEqual}
      name='milestone'
    />
  );
}
